import { useTheme } from "@/contexts/ThemeContext";
import EditorialCard from "./EditorialCard";
import { ACCENT, ACCENT_LIGHT, cardGradient } from "../homeTokens";

/**
 * The editorial row — CMS articles in an asymmetric two-by-two.
 *
 * The uneven column ratios (1.55/1 then 1/1.35) are what stop this reading as
 * a generic card grid: the eye gets a clear entry point per row instead of
 * four equal-weight rectangles competing.
 *
 * Slot styling is positional, not content-derived, so the palette stays stable
 * as the CMS rotates articles through. Only the first slot reacts to account
 * state — when something is broken, the lead card becomes the article that
 * fixes it.
 *
 * The grid collapses gracefully: with fewer than four published articles the
 * second row simply doesn't render, and a lone card spans the row rather than
 * leaving a hole.
 */

const SLOTS = [
  { from: "rgba(255,90,77,", to: "rgba(255,140,60,", accent: "danger", size: "hero" },
  { from: "rgba(123,155,255,", to: "rgba(170,110,255,", accent: "info", size: "regular" },
  { from: "rgba(92,214,178,", to: "rgba(90,140,255,", accent: "success", size: "regular" },
  { from: "rgba(255,158,205,", to: "rgba(139,139,255,", accent: "pink", size: "large" },
];

/** Lead-slot palette when a channel outage is the account's active problem. */
const CHANNEL_LEAD = {
  from: "rgba(255,164,32,",
  to: "rgba(255,96,52,",
  accent: "warning",
  size: "hero",
};

const WORDS_PER_MINUTE = 200;

function readingMinutes(body) {
  if (!body) return null;
  const words = String(body).trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

/** Turns a CMS row into the shape the card renders, deriving what it can. */
function toCardModel(item, slot, isLight) {
  const assets = item.assets || [];
  const hasVideo = assets.some((a) => a.type === "VIDEO");
  // `coverUrl` is derived server-side (first image asset, else the first image
  // embedded in the markdown body); the asset lookup is the fallback for any
  // caller still sending the raw row.
  const cover = item.coverUrl || assets.find((a) => a.type === "IMAGE")?.url || null;
  const minutes = readingMinutes(item.body || item.summary);

  const kindLabel = item.kind === "NEWS" ? "NOVIDADE" : "COMO FAZER";
  const duration = [hasVideo ? "VÍDEO" : null, minutes ? `${minutes} MIN` : null]
    .filter(Boolean)
    .join(" ");

  // Light surfaces need a denser tint and an opaque tail, or the gradient
  // washes out into the page and the card loses its edge entirely.
  const alphaFrom = isLight ? ".22)" : ".16)";
  const alphaTo = isLight ? ".13)" : ".08)";
  const tail = isLight ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.028)";

  return {
    id: item.id,
    kicker: duration ? `${kindLabel} · ${duration}` : kindLabel,
    title: item.title,
    cta: item.cta?.label || (hasVideo ? "Assistir" : "Ler o artigo"),
    coverUrl: cover,
    href: item.cta?.url || null,
    size: slot.size,
    accent: (isLight ? ACCENT_LIGHT : ACCENT)[slot.accent],
    background: cardGradient(slot.from + alphaFrom, slot.to + alphaTo, tail),
  };
}

export default function EditorialCards({ items = [], scenario, onOpen }) {
  const { resolved } = useTheme();
  const isLight = resolved === "light";

  if (!items.length) return null;

  const slots = SLOTS.map((slot, index) =>
    index === 0 && scenario === "canal" ? CHANNEL_LEAD : slot,
  );

  const cards = items
    .slice(0, 4)
    .map((item, index) => toCardModel(item, slots[index], isLight));

  const firstRow = cards.slice(0, 2);
  const secondRow = cards.slice(2, 4);

  // A single card in a row takes the whole width instead of leaving a gap.
  const rowCols = (row, ratio) =>
    row.length === 2 ? ratio : "grid-cols-1";

  return (
    <div className="flex flex-col gap-4 [perspective:1200px]">
      <div className={`grid gap-4 ${rowCols(firstRow, "md:grid-cols-[1.55fr_1fr]")}`}>
        {firstRow.map((card) => (
          <EditorialCard key={card.id} item={card} size={card.size} onOpen={onOpen} />
        ))}
      </div>
      {secondRow.length > 0 && (
        <div className={`grid gap-4 ${rowCols(secondRow, "md:grid-cols-[1fr_1.35fr]")}`}>
          {secondRow.map((card) => (
            <EditorialCard key={card.id} item={card} size={card.size} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}
